import React, { useCallback, useEffect, useState } from 'react';
import axios from 'axios';
import { Plus, Scale, Trash2 } from 'lucide-react';
import { toast } from 'sonner';
import { useAuth } from '../../contexts/AuthContext';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Badge } from '../ui/badge';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../ui/card';
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle, DialogDescription } from '../ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';

const API = process.env.REACT_APP_BACKEND_URL;
const num = (v) => Number(v || 0).toLocaleString();
const pct = (r) => `${((r || 0) * 100).toFixed(r * 100 % 1 ? 1 : 0)}%`;
const EMPTY_BRACKET = { up_to: '', rate: '', fixed: '', above: '' };
const newBracket = () => ({ ...EMPTY_BRACKET, _uid: crypto.randomUUID() });

export const StatutoryRulesTab = () => {
  const { user } = useAuth();
  const [rules, setRules] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ rule_type: 'nssf_employee', employee_type: 'resident', rate: '', effective_from: new Date().toISOString().slice(0, 10) });
  const [brackets, setBrackets] = useState([newBracket()]);
  const isAdmin = user?.role === 'super_admin';

  const load = useCallback(() => {
    axios.get(`${API}/api/payroll/rules`).then((r) => setRules(r.data.items || [])).catch(() => {});
  }, []);
  useEffect(() => { load(); }, [load]);

  const latest = (type, empType) => {
    const matches = rules.filter((r) => r.rule_type === type && r.active !== false && (!empType || r.employee_type === empType));
    return matches.sort((a, b) => (b.effective_from || '').localeCompare(a.effective_from || ''))[0];
  };

  const save = async () => {
    const body = { rule_type: form.rule_type, country: 'UG', effective_from: form.effective_from, active: true };
    if (form.rule_type === 'paye') {
      body.employee_type = form.employee_type;
      const parsed = brackets.map((b) => ({
        up_to: b.up_to === '' ? null : parseFloat(b.up_to),
        rate: parseFloat(b.rate || 0) / 100,
        fixed: parseFloat(b.fixed || 0),
        above: parseFloat(b.above || 0),
      }));
      if (!parsed.length || parsed.some((b) => Number.isNaN(b.rate))) return toast.error('Complete all bracket rows');
      body.brackets = parsed;
    } else {
      const r = parseFloat(form.rate);
      if (Number.isNaN(r) || r < 0 || r > 100) return toast.error('Enter a valid rate percentage');
      body.rate = r / 100;
    }
    try {
      await axios.post(`${API}/api/payroll/rules`, body);
      toast.success('Statutory rule added — applies to payrolls from its effective date');
      setOpen(false);
      setBrackets([newBracket()]);
      load();
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed to add rule'); }
  };

  const payeRes = latest('paye', 'resident');
  const payeNon = latest('paye', 'non_resident');
  const nssfE = latest('nssf_employee');
  const nssfR = latest('nssf_employer');
  const th = 'px-2 py-1.5 text-left text-[10px] font-semibold uppercase bg-muted border-b';
  const td = 'px-2 py-1.5 border-b text-xs tabular-nums';

  const PayeCard = ({ title, rule, testid }) => (
    <Card data-testid={testid}>
      <CardHeader className="pb-2">
        <CardTitle className="font-heading text-sm">{title}</CardTitle>
        <CardDescription className="text-xs">Effective from {rule?.effective_from || '—'}</CardDescription>
      </CardHeader>
      <CardContent className="p-0">
        <table className="w-full">
          <thead><tr>{['Monthly Income (UGX)', 'Rate', 'Fixed Amount', 'On Excess Above'].map((h) => <th key={h} className={th}>{h}</th>)}</tr></thead>
          <tbody>
            {(rule?.brackets || []).map((b, i) => (
              <tr key={`${b.up_to}-${b.rate}-${b.fixed}-${i}`}>
                <td className={td}>{b.up_to == null ? 'Above previous band' : `Up to ${num(b.up_to)}`}</td>
                <td className={td}>{pct(b.rate)}</td>
                <td className={td}>{num(b.fixed)}</td>
                <td className={td}>{num(b.above)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </CardContent>
    </Card>
  );

  return (
    <div className="space-y-4" data-testid="statutory-rules-tab">
      <div className="flex items-center justify-between">
        <div>
          <h3 className="font-heading font-semibold flex items-center gap-2"><Scale className="h-4 w-4 text-primary" /> Uganda Statutory Rules</h3>
          <p className="text-xs text-muted-foreground">PAYE brackets & NSSF rates used in payroll calculations. New rules take effect on payrolls dated on/after their effective date.</p>
        </div>
        {isAdmin && (
          <Button size="sm" onClick={() => setOpen(true)} data-testid="add-rule-btn"><Plus className="h-4 w-4 mr-1" /> Add Rule Update</Button>
        )}
      </div>

      <div className="grid md:grid-cols-2 gap-3">
        <Card data-testid="nssf-employee-card"><CardContent className="p-4">
          <p className="text-xs text-muted-foreground">NSSF — Employee Contribution</p>
          <p className="text-2xl font-heading font-bold">{nssfE ? pct(nssfE.rate) : '—'}</p>
          <p className="text-[11px] text-muted-foreground">Deducted from gross · effective {nssfE?.effective_from || '—'}</p>
        </CardContent></Card>
        <Card data-testid="nssf-employer-card"><CardContent className="p-4">
          <p className="text-xs text-muted-foreground">NSSF — Employer Contribution</p>
          <p className="text-2xl font-heading font-bold">{nssfR ? pct(nssfR.rate) : '—'}</p>
          <p className="text-[11px] text-muted-foreground">Paid by organization on top of gross · effective {nssfR?.effective_from || '—'}</p>
        </CardContent></Card>
      </div>

      <div className="grid lg:grid-cols-2 gap-3">
        <PayeCard title="PAYE — Resident Employees" rule={payeRes} testid="paye-resident-card" />
        <PayeCard title="PAYE — Non-Resident Employees" rule={payeNon} testid="paye-nonresident-card" />
      </div>

      <Card>
        <CardHeader className="pb-2"><CardTitle className="font-heading text-sm">All Rules (history)</CardTitle></CardHeader>
        <CardContent className="p-0">
          <table className="w-full">
            <thead><tr>{['Type', 'Applies To', 'Rate / Brackets', 'Effective From', 'Status'].map((h) => <th key={h} className={th}>{h}</th>)}</tr></thead>
            <tbody>
              {rules.map((r) => (
                <tr key={r.id} data-testid={`rule-row-${r.id}`}>
                  <td className={`${td} uppercase font-medium`}>{r.rule_type.replace(/_/g, ' ')}</td>
                  <td className={`${td} capitalize`}>{(r.employee_type || 'all').replace(/_/g, ' ')}</td>
                  <td className={td}>{r.rule_type === 'paye' ? `${(r.brackets || []).length} brackets` : pct(r.rate)}</td>
                  <td className={td}>{r.effective_from}</td>
                  <td className={td}><Badge variant="outline" className={r.active !== false ? 'bg-green-100 text-green-800' : 'bg-slate-100'}>{r.active !== false ? 'Active' : 'Inactive'}</Badge></td>
                </tr>
              ))}
              {rules.length === 0 && <tr><td colSpan={5} className="text-center py-6 text-muted-foreground text-sm">No rules configured</td></tr>}
            </tbody>
          </table>
        </CardContent>
      </Card>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="add-rule-dialog">
          <DialogHeader>
            <DialogTitle className="font-heading">Add Statutory Rule Update</DialogTitle>
            <DialogDescription>The newest rule per type/effective date is used automatically. Existing rules are preserved for historical payrolls.</DialogDescription>
          </DialogHeader>
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1"><Label className="text-xs">Rule Type</Label>
              <Select value={form.rule_type} onValueChange={(v) => setForm({ ...form, rule_type: v })}>
                <SelectTrigger data-testid="rule-type-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="paye">PAYE (tax brackets)</SelectItem>
                  <SelectItem value="nssf_employee">NSSF — Employee rate</SelectItem>
                  <SelectItem value="nssf_employer">NSSF — Employer rate</SelectItem>
                </SelectContent>
              </Select></div>
            <div className="space-y-1"><Label className="text-xs">Effective From</Label>
              <Input type="date" value={form.effective_from} onChange={(e) => setForm({ ...form, effective_from: e.target.value })} data-testid="rule-effective-input" /></div>
            {form.rule_type === 'paye' ? (
              <>
                <div className="space-y-1"><Label className="text-xs">Employee Type</Label>
                  <Select value={form.employee_type} onValueChange={(v) => setForm({ ...form, employee_type: v })}>
                    <SelectTrigger data-testid="rule-emptype-select"><SelectValue /></SelectTrigger>
                    <SelectContent><SelectItem value="resident">Resident</SelectItem><SelectItem value="non_resident">Non-resident</SelectItem></SelectContent>
                  </Select></div>
                <div className="col-span-2 space-y-2">
                  <Label className="text-xs">Brackets (rate as %, leave "Up to" empty for the top band)</Label>
                  {brackets.map((b, i) => (
                    <div key={b._uid} className="grid grid-cols-[1fr_1fr_1fr_1fr_auto] gap-2 items-center">
                      <Input type="number" placeholder="Up to (UGX)" value={b.up_to} onChange={(e) => setBrackets(brackets.map((x, j) => j === i ? { ...x, up_to: e.target.value } : x))} data-testid={`bracket-upto-${i}`} />
                      <Input type="number" placeholder="Rate %" value={b.rate} onChange={(e) => setBrackets(brackets.map((x, j) => j === i ? { ...x, rate: e.target.value } : x))} data-testid={`bracket-rate-${i}`} />
                      <Input type="number" placeholder="Fixed" value={b.fixed} onChange={(e) => setBrackets(brackets.map((x, j) => j === i ? { ...x, fixed: e.target.value } : x))} data-testid={`bracket-fixed-${i}`} />
                      <Input type="number" placeholder="Above" value={b.above} onChange={(e) => setBrackets(brackets.map((x, j) => j === i ? { ...x, above: e.target.value } : x))} data-testid={`bracket-above-${i}`} />
                      <Button type="button" variant="ghost" size="sm" className="h-8 px-2" onClick={() => setBrackets(brackets.filter((_, j) => j !== i))} disabled={brackets.length === 1}><Trash2 className="h-3.5 w-3.5" /></Button>
                    </div>
                  ))}
                  <Button type="button" variant="outline" size="sm" onClick={() => setBrackets([...brackets, newBracket()])} data-testid="add-bracket-btn"><Plus className="h-3.5 w-3.5 mr-1" /> Add Bracket</Button>
                </div>
              </>
            ) : (
              <div className="space-y-1"><Label className="text-xs">Rate (%)</Label>
                <Input type="number" step="0.1" value={form.rate} onChange={(e) => setForm({ ...form, rate: e.target.value })} placeholder="e.g. 5" data-testid="rule-rate-input" /></div>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)}>Cancel</Button>
            <Button onClick={save} data-testid="rule-save-btn">Save Rule</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default StatutoryRulesTab;
